import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router";
import projectApi from "src/api/project/project";
import projectInstanceApi from "src/api/project/projectInstance";
import {DateCalendar, PickersDay} from '@mui/x-date-pickers';
import {apiDateTimeStringToDate} from "src/api/apiDateTimeHandler";
import {format} from "date-fns";
import ProjectInstanceList from "src/components/project/ProjectInstanceList";
import {
  Button, Dialog, DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle, FormHelperText,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import Box from "@mui/material/Box";
import appointmentApi from "src/api/project/appointment";
import {handleApiError} from "src/api/errorHandler";
import {handleInputChange} from "src/components/form/formHandler";
import projectEligibilityApi from "src/api/project/projectEligibility";

const ProjectMakeAppointmentPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [projectInstances, setProjectInstances] = useState(null);
  const [projectInstancesGroupedByDate, setProjectInstancesGroupedByDate] = useState(null);
  const [projectEligibility, setProjectEligibility] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointmentLoaded, setAppointmentLoaded] = useState(false);
  // This will contain the appointment the user already might have when loading the page
  const [appointment, setAppointment] = useState(null);
  // This will contain the project instance the user selects on the page
  const [selectedProjectInstance, setSelectedProjectInstance] = useState(null);
  const [formValues, setFormValues] = useState({
    'comment': '',
  })
  const [confirmShow, setConfirmedShow] = useState(false);
  const [createdAppointment, setCreatedAppointment] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  const fetchProject = async () => {
    const fetchedProject = await projectApi.retrieveItem(id);
    setProject(fetchedProject);
  }

  const fetchProjectInstancesOfProject = async (project, quota = null) => {
    const params = {};
    if (quota !== null && quota !== 0) {
      params.quota = quota;
    }
    params['start[strictly_after]'] = new Date();
    const fetchedProjectInstances = await projectInstanceApi.retrieveCollectionByProject(project.id, params);
    setProjectInstances(fetchedProjectInstances['hydra:member']);
  }

  const fetchProjectEligibility = async (project) => {
    const fetchedProjectEligibilities = await projectEligibilityApi.retrieveCollectionByProject(project.id);
    setProjectEligibility(fetchedProjectEligibilities['hydra:member'] ? fetchedProjectEligibilities['hydra:member'][0] : null);
  }

  const fetchAppointment = async (project) => {
    setAppointmentLoaded(false);
    const fetchedAppointment = await appointmentApi.retrieveCollection({project: project['@id']});
    setAppointment(fetchedAppointment['hydra:member'] ? fetchedAppointment['hydra:member'][0] : null);
    setAppointmentLoaded(true);
  }

  const groupProjectInstancesByDate = (projectInstances) => {
    const result = {};

    projectInstances.forEach((projectInstance) => {
      const start = apiDateTimeStringToDate(projectInstance.start);
      const formattedStart = format(start, 'yyyy-MM-dd');
      if (result.hasOwnProperty(formattedStart)) {
        result[formattedStart].push(projectInstance);
        return;
      }
      result[formattedStart] = [projectInstance];
    })

    setProjectInstancesGroupedByDate(result);
  }

  useEffect(() => {
    fetchProject();
  }, [])

  useEffect(() => {
    if (project) {
      fetchAppointment(project);
      fetchProjectEligibility(project);
    }
  }, [project])

  useEffect(() => {
    if (project && projectEligibility) {
      fetchProjectInstancesOfProject(project, projectEligibility.quota)
    }
  }, [project, projectEligibility]);

  useEffect(() => {
    if (projectInstances) {
      groupProjectInstancesByDate(projectInstances);
    }
  }, [projectInstances])

  const renderDay = ({ day, ...otherProps}) => {
    return (
      <PickersDay
        day={day}
        {...otherProps}
        sx={{
          bgcolor: projectInstancesGroupedByDate && Object.keys(projectInstancesGroupedByDate).includes(format(day, 'yyyy-MM-dd')) ? 'secondary.main' : 'inherit',
        }}
      />
    )
  }

  const navigateToProjectDetail = () => {
    navigate(`/projecten/${project.id}`);
  }

  const makeAppointment = async () => {
    setFormErrors({});
    try {
      const createdAppointment = await appointmentApi
        .create({
          projectInstance: selectedProjectInstance['@id'],
          comment: formValues.comment,
        });
      setCreatedAppointment(createdAppointment);

      setConfirmedShow(true);
    }
    catch (error) {
      handleApiError(error, setFormErrors);
    }
  }

  const toProject = () => {
    navigate(`/projecten/${id}`)
  }

  if (!appointmentLoaded) {
    return null;
  }

  // User already has an appointment for this project
  if (appointmentLoaded && appointment) {
    return (
      <>
        <Typography variant="body1" mb={3}>Je hebt al een afspraak voor dit project. Mocht je deze willen wijzigen, annuleer dan eerst de huidige afspraak op de project pagina. Daarna kun je een nieuwe afspraak maken.</Typography>
        <Button variant="contained" onClick={navigateToProjectDetail}>Naar de project pagina</Button>
      </>
    );
  }

  let resetFormErrors = () => setFormErrors({});
  return (
    <>
      <Typography variant="h2" mb={2}>{project.name}</Typography>
      <Typography variant="body1" mb={2}>Hier kun je een afspraak maken voor deze test. Je kunt de kalender gebruiken om de verschillende ingeplande testdagen en tijden te vinden. Klik altijd op “afspraak maken” om een plekje te reserveren.</Typography>
      <Typography variant="h3" mb={2}>Kies een datum en tijd.</Typography>
      <Grid2 container>
        <Grid2 xs={12} md={6}>
          <DateCalendar
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            slots={{
              day: renderDay
            }}
            loading={!projectInstancesGroupedByDate}
            disablePast
          />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <Stack dir="column" spacing={2}>
            <Box>
              <Typography component="span" variant="subtitle1" sx={{ textTransform: 'capitalize' }} color="secondary">{format(selectedDate, 'EEEE d LLLL yyyy')}</Typography>
              {
                projectInstancesGroupedByDate
                  ? (
                    <ProjectInstanceList
                      projectInstances={projectInstancesGroupedByDate[format(selectedDate, 'yyyy-MM-dd')]}
                      selectedProjectInstance={selectedProjectInstance}
                      setSelectedProjectInstance={setSelectedProjectInstance}
                      onChange={resetFormErrors}
                    />
                  )
                  : <Skeleton />
              }
            </Box>
            <TextField
              value={formValues.comment}
              onChange={(e) => handleInputChange(e, formValues, setFormValues)}
              name="comment"
              label="Opmerking"
              error={'comment' in formErrors}
              helperText={formErrors.comment}
              multiline
              minRows={3}
            />
            <Button
              variant="contained"
              disabled={!selectedProjectInstance}
              onClick={makeAppointment}
            >
              Afspraak maken
            </Button>
            { formErrors.projectInstance && <FormHelperText error>{formErrors.projectInstance}</FormHelperText>}
          </Stack>
        </Grid2>
      </Grid2>
      <Dialog
        open={confirmShow}
        aria-labelledby="appointment-confirmed-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Afspraak gemaakt
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {createdAppointment
              ? `Je hebt een afspraak gemaakt voor ${format(apiDateTimeStringToDate(createdAppointment.projectInstance.start), 'd MMMM yyyy kk:mm')}. Je ontvangt hiervan nog een bevesting per e-mail.`
              : null
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={toProject}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProjectMakeAppointmentPage;

ProjectMakeAppointmentPage.propTypes = {}

ProjectMakeAppointmentPage.defaultProps = {}